import { Tooltip } from 'antd';
import { Ban, Check, X } from 'lucide-react';
import { chipBackground, readableTagColor, tagShortName, type TagOption } from './types';

interface Props {
    tag?: TagOption;
    toTagId: number;
    allowed: boolean;
    /** Đã sửa so với cấu hình đã lưu -> hiện chấm cam */
    changed: boolean;
    disabled?: boolean;
    isDark: boolean;
    onToggle: () => void;
    onRemove: () => void;
}

/**
 * Chip vừa là nhãn vừa là công tắc: bấm vào chip để bật/tắt đích,
 * rê chuột mới hiện dấu × để xóa hẳn khỏi ma trận.
 */
export default function TransitionChip({
    tag,
    toTagId,
    allowed,
    changed,
    disabled,
    isDark,
    onToggle,
    onRemove,
}: Props) {
    const color = readableTagColor(tag?.color, isDark);
    const name = tagShortName(tag, toTagId);
    const code = tag?.code;

    return (
        <div className="relative group/chip inline-flex">
            <Tooltip title={allowed ? 'Đang bật — bấm để tắt' : 'Đang tắt — bấm để bật'}>
                <button
                    type="button"
                    disabled={disabled}
                    aria-pressed={allowed}
                    onClick={onToggle}
                    className={[
                        'inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 text-xs font-medium',
                        'max-w-[260px] transition-colors border',
                        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                        allowed
                            ? 'border-transparent hover:border-current'
                            : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 bg-transparent dark:border-[#3a3a3a] dark:text-[#6b6b6b] dark:hover:border-[#5a5a5a] dark:hover:text-[#a0a0a0]',
                    ].join(' ')}
                    style={
                        allowed
                            ? { backgroundColor: chipBackground(tag?.color, isDark), color }
                            : undefined
                    }
                >
                    {allowed ? (
                        <Check className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                        <Ban className="w-3.5 h-3.5 shrink-0" />
                    )}
                    {code && (
                        <span className={`font-mono text-[11px] shrink-0 ${allowed ? '' : 'line-through'}`}>
                            {code}
                        </span>
                    )}
                    <span className={`truncate ${allowed ? '' : 'line-through'}`}>{name}</span>
                </button>
            </Tooltip>

            {!disabled && (
                <Tooltip title="Xóa đích này khỏi ma trận">
                    <button
                        type="button"
                        aria-label={`Xóa đích ${code || name}`}
                        onClick={onRemove}
                        className="absolute -right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover/chip:opacity-100 focus:opacity-100
                                   w-4 h-4 rounded-full grid place-items-center bg-white text-gray-400
                                   hover:!text-red-500 hover:bg-red-50 shadow-sm transition-opacity
                                   dark:bg-[#1f1f1f] dark:text-[#8a8a8a] dark:hover:bg-[#3a1d1b]"
                    >
                        <X className="w-2.5 h-2.5" />
                    </button>
                </Tooltip>
            )}

            {changed && (
                <span
                    title="Thay đổi chưa lưu"
                    className="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-white dark:ring-[#141414]"
                />
            )}
        </div>
    );
}
